import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import adminApiClient, {
  KlydoMonitor,
  MonitorStatus,
} from "../../../api/admin";

const QUERY_KEY = ["klydo-monitors"];

export const useKlydoMonitors = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminApiClient.getKlydoMonitors(),
    refetchInterval: 30000,
  });
};

export const useHasUnacknowledgedMonitorFailures = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminApiClient.getKlydoMonitors(),
    refetchInterval: 30000,
    select: (monitors) =>
      monitors.some((monitor) => {
        const isSnoozed =
          monitor.snoozeUntil !== null && monitor.snoozeUntil > Date.now();
        return (
          monitor.status === MonitorStatus.Failed &&
          !monitor.acknowledge &&
          !monitor.resolved &&
          monitor.active !== false &&
          !isSnoozed
        );
      }),
  });
};

export const useAvailableMonitorNames = () =>
  useQuery({
    queryKey: ["available-monitor-names"],
    queryFn: () => adminApiClient.getAvailableMonitorNames(),
  });

export const useCreateMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<KlydoMonitor>) =>
      adminApiClient.createKlydoMonitor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["available-monitor-names"] });
    },
  });
};

export const useTriggerMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, chunkIndex }: { id: string; chunkIndex?: number }) =>
      adminApiClient.triggerKlydoMonitor(id, chunkIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useSnoozeMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      durationHours,
    }: {
      id: string;
      durationHours: number;
    }) => adminApiClient.snoozeKlydoMonitor(id, durationHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const usePauseMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.pauseKlydoMonitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useResumeMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.resumeKlydoMonitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useUpdateMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<KlydoMonitor> }) =>
      adminApiClient.updateKlydoMonitor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useDeleteMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.deleteKlydoMonitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useAcknowledgeMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.acknowledgeKlydoMonitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useResolveMonitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApiClient.resolveKlydoMonitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};
